import { inject } from '@angular/core';
import { tap } from 'rxjs';
import { patchState, signalStore, withMethods } from '@ngrx/signals';
import { updateEntity, withEntities } from '@ngrx/signals/entities';
import {
  fetchAllPages,
  withCursorPagination,
  withPickerList,
} from '@dgacontrol-frontend/shared-util-cursor-pagination';

import { CellularFacadeService } from './cellular-facade.service';
// Self-contained, like every data-access lib: the store speaks the Orval-GENERATED
// types, NOT `@dgacontrol-frontend/core`. The app components bridge generated↔core.
import type {
  CellularV2MobileOperatorsListParams,
  MobileOperatorCreateRequestV2Request,
  PatchedMobileOperatorUpdateRequestV2Request,
  MobileOperatorV2,
} from './generated/types';

export const MobileOperatorStore = signalStore(
  withEntities<MobileOperatorV2>(),
  // Table: v2 cursor list (load = page 1, loadMore = next cursor). COMPONENT-DRIVEN
  // (dt-mobile-operator calls load({}) on init) — the SIM card picker consumes the
  // dropdown slice below, not the table slice, so nothing should auto-load.
  withCursorPagination<MobileOperatorV2, CellularV2MobileOperatorsListParams>(
    () => {
      const facade = inject(CellularFacadeService);
      return (query, cursor) =>
        facade.getMobileOperatorsPage({ ...query, cursor: cursor ?? undefined });
    },
  ),
  // Picker list (the SIM card carrier dropdowns), SEPARATE from the partial cursor
  // entities() (store-conventions §"Tabla cursor + dropdowns").
  withPickerList('mobileOperators', () => {
    const facade = inject(CellularFacadeService);
    return () =>
      fetchAllPages((cursor) => facade.getMobileOperatorsPage({ page_size: 1000, cursor }));
  }),
  withMethods((store, facade = inject(CellularFacadeService)) => ({
    // v2 create → single `{mobile_operator}`. Do NOT addEntity into a cursor list:
    // the new row's server-side sort position is unknown → inserting locally
    // desyncs the cursor → dup/missing rows on the next loadMore. Reset to page 1
    // via the cursor feature's `load` (server-authoritative).
    createMobileOperator(body: MobileOperatorCreateRequestV2Request) {
      return facade.createMobileOperator(body).pipe(
        tap(() => {
          store.load({});
        }),
      );
    },
    // v2 update → single `{mobile_operator}` → patch the existing row IN PLACE
    // (idKey = `id`, the default withEntities selectId; the path param `identity`
    // is only the URL). The cursor stays valid (no reorder).
    updateMobileOperator(
      body: PatchedMobileOperatorUpdateRequestV2Request,
      identity: string,
    ) {
      return facade.updateMobileOperator(body, identity).pipe(
        tap((res) => {
          patchState(
            store,
            updateEntity({
              id: res.mobile_operator.id,
              changes: res.mobile_operator,
            }),
          );
        }),
      );
    },
  })),
);
